import { useQueryClient } from "@tanstack/react-query";
import useProfile from "./useProfile";
import { useState } from "react";
import { useFirebase } from "../../wrappers/firebase";
import {
  arrayRemove,
  arrayUnion,
  documentId,
  increment,
  orderBy,
  QueryDocumentSnapshot,
  serverTimestamp,
  where,
} from "firebase/firestore";
import useUserStore from "../../store/userStore";
import { shallow } from "zustand/shallow";
/**
 * Hook to manage a user's playlist stored as a sub-collection under `profiles/{uid}/playlist`.
 * Each playlist entry contains: { username, userid, postId }.
 */
export default () => {
  const { getData, addDocument, updateDocument } = useFirebase();
  const queryClient = useQueryClient();

  const { getProfile } = useProfile();
  const { uid, username: stateUsername } = useUserStore(
    (state) => state.user,
    shallow
  );
  const [isFetching, setIsFetching] = useState(false);

  async function getPlaylist({ username, playlistId }) {
    setIsFetching(true);
    const { id: userId } = await getProfile({ username });
    let playlist = queryClient.getQueryData([
      "playlists",
      username,
      playlistId,
    ]);

    if (!playlist) {
      console.log("Playlist is not in cache");
      playlist = await getData({
        path: `profiles/${userId}/playlists/${playlistId}`,
      });
      if (!playlist) throw new Error("Playlist does not exist");
      queryClient.setQueryData(
        ["playlists", username, playlistId],
        () => playlist
      );
    }
    setIsFetching(false);
    return playlist;
  }

  async function listPlaylistsByUser({ username }) {
    setIsFetching(true);
    const { id: userId } = await getProfile({ username });

    const _playlistsQuery = queryClient.getQueriesData({
      queryKey: ["playlists", username],
    });
    const _cachedPlaylists = _playlistsQuery
      ?.map(([, data]) => data)
      .flat()
      .filter(
        (item) =>
          item &&
          typeof item === "object" &&
          !Array.isArray(item) &&
          // Exclude Firestore QueryDocumentSnapshot instances
          !(typeof item.get === "function" && typeof item.data === "function")
      );

    const lastPlaylist = queryClient.getQueryData([
      "playlists",
      username,
      "lastPlaylistRef",
    ]);
    const { docs: playlists, last: lastPlaylistRef } = await getData({
      path: `profiles/${userId}/playlists`,
      limit: 10,
      filters: [orderBy("createdAt", "desc"), where("isDeleted", "==", false)],
      startAfterDoc: lastPlaylist,
    });

    playlists?.forEach((playlist) => {
      queryClient.setQueryData(
        ["playlists", username, playlist.id],
        (oldData = {}) => ({ ...oldData, ...playlist })
      );
    });

    if (_cachedPlaylists?.length > 0) {
      return [..._cachedPlaylists, ...(playlists || [])];
    }

    queryClient.setQueryData(
      ["playlists", username, "lastPlaylistRef"],
      () => lastPlaylistRef
    );

    setIsFetching(false);
    return playlists;
  }

  async function addNewPlaylist() {
    const data = {
      name: "New Playlist",
      createdAt: serverTimestamp(),
      isDeleted: false,
      isPublic: true,
    };
    const playlist = await addDocument({
      path: `profiles/${uid}/playlists`,
      data,
    });
    queryClient.setQueryData(
      ["playlists", stateUsername, playlist.id],
      (oldData = {}) => ({ ...oldData, ...playlist })
    );

    return playlist;
  }

  async function copyPlaylist({ username, playlistObject }) {
    const { id: userId } = await getProfile({ username });
    const _updatedPlaylistObject = {
      ...playlistObject,
      createdAt: serverTimestamp(),
      copyCount: 0,
    };
    delete _updatedPlaylistObject.id;

    const playlist = await addDocument({
      path: `profiles/${uid}/playlists`,
      data: _updatedPlaylistObject,
    });

    await updateDocument({
      path: `profiles/${userId}/playlists/${playlistObject.id}`,
      data: {
        copyCount: increment(1),
      },
    });
    queryClient.setQueryData(
      ["playlists", username, playlistObject.id],
      (oldData = {}) => ({ ...oldData, copyCount: oldData.copyCount + 1 })
    );

    queryClient.setQueryData(
      ["playlists", stateUsername, playlist.id],
      (oldData = {}) => ({ ...oldData, ...playlist })
    );

    return playlist;
  }

  function updatePlaylist({ playlistId, data }) {
    queryClient.setQueryData(
      ["playlists", stateUsername, playlistId],
      (oldData) => ({
        ...oldData,
        ...data,
      })
    );

    (async () => {
      try {
        await updateDocument({
          path: `profiles/${uid}/playlists/${playlistId}`,
          data,
        });
      } catch (err) {
        console.error("Failed to update playlist on db:", err);
        // optionally: rollback queryClient.setQueryData here
      }
    })();

    return data;
  }

  async function deletePlaylist({ playlistId }) {
    try {
      await updateDocument({
        path: `profiles/${uid}/playlists/${playlistId}`,
        data: {
          isDeleted: true,
          deletedAt: serverTimestamp(),
        },
      });
      queryClient.removeQueries({
        queryKey: ["playlists", stateUsername, playlistId],
      });
    } catch (err) {
      console.error("Failed to update playlist on db:", err);
      // optionally: rollback queryClient.setQueryData here
    }
  }

  async function addItemToPlaylist({ playlistId, username, postId }) {
    const { id: userId } = await getProfile({ username });

    await updateDocument({
      path: `profiles/${uid}/playlists/${playlistId}`,
      data: {
        items: arrayUnion({
          userId,
          postId,
        }),
      },
    });

    queryClient.setQueryData(
      ["playlists", stateUsername, playlistId],
      (oldData = {}) => {
        const items = Array.isArray(oldData?.items)
          ? oldData.items.slice()
          : [];
        // Check if item already exists
        const exists = items.some(
          (item) => item.userId === userId && item.postId === postId
        );
        if (!exists) {
          items.push({ userId, postId });
        }
        return {
          ...oldData,
          items,
        };
      }
    );
  }

  /**
   *
   * @param {Object} props
   * @param {string} props.playlistId
   * @param {{userId: string, postId: string}} props.item
   */
  function removeItemFromPlaylist({ playlistId, item }) {
    let updatedItems = [];

    queryClient.setQueryData(
      ["playlists", stateUsername, playlistId],
      (oldData = {}) => {
        const items = Array.isArray(oldData?.items)
          ? oldData.items.slice()
          : [];
        const filteredItems = items.filter(
          (_item) =>
            !(_item.userId === item.userId && _item.postId === item.postId)
        );
        updatedItems = filteredItems;
        return {
          ...oldData,
          items: filteredItems,
        };
      }
    );

    (async () =>
      await updateDocument({
        path: `profiles/${uid}/playlists/${playlistId}`,
        data: {
          items: arrayRemove(item),
        },
      }))();

    return updatedItems;
  }

  return {
    listPlaylistsByUser,
    getPlaylist,
    addNewPlaylist,
    addItemToPlaylist,
    updatePlaylist,
    removeItemFromPlaylist,
    copyPlaylist,
    deletePlaylist,
    isFetching
    // queries
  };
};
